import React, { useState, useEffect, useRef, useCallback } from "react";
import { Icon, Button, Popup } from "semantic-ui-react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useAuth } from "../../../hooks";
import { useLanguage } from "../../../contexts";
import { useCompany } from "../../../contexts/CompanyContext";
import { Sbti } from "../../../api";
import { BasicModal } from "../../../components/Shared";
import {
  Dashboard,
  Paso1Inventario,
  Paso2Screening,
  Paso3Ruta,
  Paso4Objetivos,
  Paso5Checklist,
  Paso6Tramite,
  Paso7Seguimiento,
  Guia,
  Fuentes,
} from "../../../components/Admin/SbtiForms";
import "./SbtiForms.scss";

const sbtiController = new Sbti();

// Portado de VIEWS del prototipo (Sustemia_SBTi_v1_2026-08-03.html, línea
// ~397). "built" marca qué pasos tienen componente real — con Guía y
// Fuentes ya están las 12 pantallas del backlog (F3+F4+ estas dos de
// F6). Lo único que sigue pendiente de F6 es el seed de demo y la
// auditoría de paridad final (bloqueados, ver conversación).
const NAV = [
  { grp: "Panorama" },
  { id: "dash", n: "◈", t: "Hoja de ruta SBTi", built: true },
  { grp: "Desarrollo del objetivo" },
  { id: "p1", n: "1", t: "Inventario GEI · año base", built: true },
  { id: "p2", n: "2", t: "Screening Alcance 3 + FLAG", built: true },
  { id: "p3", n: "3", t: "Ruta: corporativa o PYME", built: true },
  { id: "p4", n: "4", t: "Diseño de objetivos", built: true },
  { id: "p5", n: "5", t: "Autochequeo de criterios", built: true },
  { grp: "Validación y vida del objetivo" },
  { id: "p6", n: "6", t: "Presentación y validación", built: true },
  { id: "p7", n: "7", t: "Seguimiento y revisión", built: true },
  { grp: "Referencia" },
  { id: "guia", n: "G", t: "Guía SBTi para fábricas", built: true },
  { id: "fuentes", n: "§", t: "Fuentes y trazabilidad", built: true },
];

// Portado de stepStatus() del prototipo (línea ~414) — mismas condiciones,
// leyendo de `record` (schema nuevo) y del bloque `_computed` en vez de
// llamar calc()/critList() locales: el motor vive en un solo lugar
// (server/services/sbtiEngine.js), el cliente solo lee su resultado.
function stepStatus(id, record, computed) {
  if (!record || !computed) return "";
  const c = computed.calc;
  const params = computed.params || {};

  if (id === "p1") {
    return record.inventory?.baseYear && c.scope1 + c.scope2 > 0 ? "ok" : record.inventory?.baseYear ? "mid" : "";
  }
  if (id === "p2") {
    if (!(c.scope3Total > 0)) return "";
    if (!c.scope3Required) return "ok";
    return c.coveragePercent >= (params.cobertura_a3 ?? 67) ? "ok" : "mid";
  }
  if (id === "p3") return record.route?.type ? "ok" : "";
  if (id === "p4") {
    const has12 = record.targets?.scope12?.targetYear;
    const has3 = record.targets?.scope3?.targetYear;
    return has12 && (!c.scope3Required || has3) ? "ok" : has12 ? "mid" : "";
  }
  if (id === "p5") {
    const items = (computed.criteria || []).filter((x) => !x.group);
    const criteria = record.criteria || {};
    const done = items.filter((x) => ["ok", "na"].includes(criteria[x.id]?.status)).length;
    return done === items.length && items.length > 0 ? "ok" : done > 0 ? "mid" : "";
  }
  if (id === "p6") return record.validation?.approvedOn ? "ok" : record.validation?.submittedOn ? "mid" : "";
  if (id === "p7") return (record.tracking || []).length ? "ok" : "";
  return "";
}

function ComingSoon({ title }) {
  return (
    <div className="sbti-coming-soon">
      <Icon name="hourglass half" size="huge" />
      <h3>{title}</h3>
      <p>Esta pantalla se construye en una fase siguiente del módulo. La navegación ya está completa; el contenido se va sumando fase por fase.</p>
    </div>
  );
}

export function SbtiForms() {
  const { accessToken, user } = useAuth();
  const { translations } = useLanguage();
  const t = (key) => translations[key] || key;

  const companyContext = useCompany().company;
  const company = companyContext || user?.company;

  const [currentStep, setCurrentStep] = useState("dash");
  const [record, setRecord] = useState(null);
  const [computed, setComputed] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showDataModal, setShowDataModal] = useState(false);
  const [importing, setImporting] = useState(false);

  const debounceTimers = useRef({});

  // Mínimo de tiempo que se muestra el spinner del logo, para que no
  // parpadee cuando la respuesta llega casi al instante (ej. caché).
  const MIN_LOADING_MS = 1500;

  const loadExpediente = useCallback(async () => {
    if (!company) return;
    setLoading(true);
    const startedAt = Date.now();
    try {
      const response = await sbtiController.getExpediente(accessToken, company);
      const { _computed, ...rest } = response;
      setRecord(rest);
      setComputed(_computed);
    } catch (error) {
      console.error(error);
      toast.error(t("sbti_load_error") || "No se pudo cargar el expediente SBTi.");
    } finally {
      const remaining = MIN_LOADING_MS - (Date.now() - startedAt);
      if (remaining > 0) await new Promise((resolve) => setTimeout(resolve, remaining));
      setLoading(false);
    }
  }, [accessToken, company]);

  useEffect(() => {
    loadExpediente();
  }, [loadExpediente]);

  // Guarda una sección con debounce de 400ms — mismo criterio de
  // persistencia que el resto de Sustemia (hoja 06 del workbook:
  // "Autosave con debounce 400 ms en change/input").
  const patchSection = useCallback(
    (section, data, { immediate = false } = {}) => {
      setRecord((prev) => ({ ...prev, [section]: data }));

      const fire = async () => {
        setSaving(true);
        try {
          const response = await sbtiController.patchSection(accessToken, company, section, data);
          const { _computed, ...rest } = response;
          setRecord(rest);
          setComputed(_computed);
        } catch (error) {
          console.error(error);
          toast.error(t("sbti_save_error") || "No se pudo guardar el cambio.");
        } finally {
          setSaving(false);
        }
      };

      clearTimeout(debounceTimers.current[section]);
      if (immediate) fire();
      else debounceTimers.current[section] = setTimeout(fire, 400);
    },
    [accessToken, company]
  );

  // Portado de exportJSON()/importJSON() del prototipo (botón "Datos ⇅"
  // del header). No se porta la importación por pegado de JSON del
  // módulo GEI (openIO → "Importar desde GEI"): esa vía la reemplaza el
  // botón Sincronizar del Paso 1, que habla con el endpoint real en vez
  // de un JSON pegado a mano. Tampoco se porta "Borrar todo": es
  // destructivo y no hay endpoint de reset — se puede sumar si hace falta.
  const handleExport = async () => {
    try {
      const response = await sbtiController.exportExpediente(accessToken, company);
      const blob = new Blob([JSON.stringify(response, null, 2)], { type: "application/json" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `Sustemia_SBTi_${company}_${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success("JSON exportado");
    } catch (error) {
      console.error(error);
      toast.error("No se pudo exportar el expediente.");
    }
  };

  const handleImportFile = (event) => {
    const file = event.target.files[0];
    event.target.value = ""; // permite reimportar el mismo archivo dos veces seguidas
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async () => {
      setImporting(true);
      try {
        const data = JSON.parse(reader.result);
        await sbtiController.importExpediente(accessToken, company, data);
        await loadExpediente();
        toast.success("Expediente importado");
        setShowDataModal(false);
      } catch (error) {
        console.error(error);
        toast.error(error?.msg || "El archivo no es un JSON válido del módulo, o falló la importación.");
      } finally {
        setImporting(false);
      }
    };
    reader.readAsText(file);
  };

  if (!company) {
    return (
      <div className="sbti-page">
        <div className="no-site-warning">
          <Icon name="warning sign" size="big" />
          <p>{t("select_company_site_from_header") || "Seleccioná una empresa desde el encabezado."}</p>
        </div>
      </div>
    );
  }

  if (loading || !record) {
    return (
      <div className="sbti-page sbti-page--loading">
        <div className="sbti-loading-spinner">
          <img
            src={`${process.env.PUBLIC_URL}/favicon.png`}
            alt="sustemia"
            className="sbti-loading-spinner__logo"
          />
          <span className="sbti-loading-spinner__text">Cargando expediente SBTi…</span>
        </div>
      </div>
    );
  }

  const stepProps = { record, computed, patchSection, saving, reload: loadExpediente, accessToken, company, t };

  return (
    <>
      <div className="sbti-topbar">
        <Button basic size="small" onClick={() => setShowDataModal(true)}>
          <Icon name="exchange" /> Datos ⇅
        </Button>
        <Popup
          content="Generar informe está temporalmente deshabilitado — no abre el informe todavía."
          trigger={
            <span>
              <Button primary size="small" disabled>
                <Icon name="file alternate outline" /> Generar informe
              </Button>
            </span>
          }
        />
      </div>

      <BasicModal show={showDataModal} close={() => setShowDataModal(false)} size="small">
        <div className="sbti-data-modal">
          <h3>Datos del módulo — respaldo e integración</h3>
          <div className="sbti-note">
            Persistencia en tres capas, igual que el resto de Sustemia: <b>autoguardado local</b> en este
            navegador, <b>export/import JSON</b> para compartir con el equipo, e <b>informe descargable</b> para
            reportar. Los datos de actividad detrás del inventario provienen del módulo GEI, donde el sistema los
            carga automáticamente a partir de las evidencias (facturas de energía, manifiestos, certificados, etc.).
          </div>

          <div className="sbti-data-modal__row">
            <div>
              <label>Exportar estado completo del módulo</label>
              <Button className="sbti-btn sbti-btn--outline" onClick={handleExport}>Descargar JSON</Button>
            </div>
            <div>
              <label>Importar estado (JSON del módulo SBTi)</label>
              <input type="file" accept=".json,application/json" onChange={handleImportFile} disabled={importing} />
            </div>
          </div>

          <hr className="sbti-data-modal__divider" />

          <label>Importar inventario desde el módulo GEI de Sustemia (pegar JSON exportado)</label>
          <textarea
            className="sbti-data-modal__json"
            disabled
            placeholder='Formato esperado (flexible): {"anio":2025,"alcance1":1234.5,"alcance2_market":2100,"alcance2_location":2350,"alcance3":{"cat1":8000,"cat4":600,...}}  — también reconoce claves s1/s2/scope1/a1, etc.'
          />
          <p className="sbti-hint">
            El mapeador reconoce claves habituales del export del módulo GEI (alcance1/s1/scope1;
            alcance2_market/s2m; alcance3 por categoría cat1…cat15). Lo que no reconozca queda para carga manual en
            el Paso 1 — nunca inventa valores. <b>Deshabilitado por ahora:</b> el sync real con el módulo GEI vive
            en el botón «Sincronizar» del Paso 1 — ver server/services/sbtiGeiFetcher.js.
          </p>

          <div className="sbti-data-modal__foot">
            <Button className="sbti-btn sbti-btn--danger" disabled title="Sin endpoint de borrado todavía">Borrar todo</Button>
            <span className="sbti-data-modal__spacer" />
            <Button className="sbti-btn sbti-btn--outline" onClick={() => setShowDataModal(false)}>Cerrar</Button>
            <Button className="sbti-btn sbti-btn--solid" disabled title="Reemplazado por el sync del Paso 1">Importar inventario GEI</Button>
          </div>
        </div>
      </BasicModal>
      <div className="sbti-page">
      <nav className="sbti-side">
        {NAV.map((v, i) =>
          v.grp ? (
            <div className="sbti-side__grp" key={`grp-${i}`}>
              {v.grp}
            </div>
          ) : (
            <button
              key={v.id}
              type="button"
              className={`sbti-side__item${currentStep === v.id ? " on" : ""}`}
              onClick={() => setCurrentStep(v.id)}
            >
              <span className="sbti-side__n">{v.n}</span>
              <span className="sbti-side__t">{v.t}</span>
              <span className={`sbti-side__st ${stepStatus(v.id, record, computed)}`} />
            </button>
          )
        )}
      </nav>

      <main className="sbti-main">
        {saving && <div className="sbti-saving">Guardando…</div>}
        {currentStep === "dash" && <Dashboard {...stepProps} onNavigate={setCurrentStep} />}
        {currentStep === "p1" && <Paso1Inventario {...stepProps} />}
        {currentStep === "p2" && <Paso2Screening {...stepProps} />}
        {currentStep === "p3" && <Paso3Ruta {...stepProps} />}
        {currentStep === "p4" && <Paso4Objetivos {...stepProps} />}
        {currentStep === "p5" && <Paso5Checklist {...stepProps} />}
        {currentStep === "p6" && <Paso6Tramite {...stepProps} />}
        {currentStep === "p7" && <Paso7Seguimiento {...stepProps} />}
        {currentStep === "guia" && <Guia {...stepProps} />}
        {currentStep === "fuentes" && <Fuentes {...stepProps} />}
        {!["dash", "p1", "p2", "p3", "p4", "p5", "p6", "p7", "guia", "fuentes"].includes(currentStep) && (
          <ComingSoon title={NAV.find((v) => v.id === currentStep)?.t} />
        )}
      </main>
      </div>
    </>
  );
}
